/**
 * @file: 坐标系统高级查询
 * @author: xiaolong.huang@dmall.com
 */

import * as Toolkit from '../toolkit/index';
import * as CubeTree from '../cubeTree/index';
import Utils from '../utils/util';
import Config from '../config/index';
import Store from './store/index';
import { paintToLogic } from './transform';

/**
 * 获取目标矩形区域内的cubeid列表
 * todo: 性能优化，没必要全量查找
 */
function getInTargetLogicRectCubeIds(rect: LogicCoordinate): Array<CubeId> {
    let cubeIds: Array<string> = Store.allCubeIds();

    return cubeIds.filter((cubeId: string): boolean => {
        let beContainRect: LogicCoordinate = Store.getLogicCoordinate(cubeId);

        return Toolkit.beContainHalf(beContainRect, rect);
    });
}

// 获取指定坐标范围内的cube
function getInTargetPaintRectCubeIds(rect: PaintCoordinate): Array<CubeId> {
    let cubeIds: Array<string> = Store.allCubeIds();

    return cubeIds.filter((cubeId: CubeId): boolean => {
        let beContainRect: PaintCoordinate = Store.getPaintCoordinate(cubeId);

        return Toolkit.beContainHalf(beContainRect, rect);
    });
}

function filterByTargetPaintRect(rect: PaintCoordinate, cubeIds: CubeId[] = []): Array<CubeId> {
    let logicRect: LogicCoordinate = paintToLogic(rect);

    return cubeIds.filter((cubeId: CubeId): boolean => {
        let beContainRect: LogicCoordinate|null = Store.getLogicCoordinate(cubeId);

        return Toolkit.beContainHalf(beContainRect, logicRect);
    });
}

// 相对物理坐标获取
function getRelativePhysicsCoordinate(targetCube: CubeId): PhysicsRelativeCoordinate|null {
    if (CubeTree.isInStage(targetCube)) {
        let physicsCoordinate: PhysicsCoordinate|null = Store.getPhysicsCoordinate(targetCube);

        if (!physicsCoordinate) {
            return null;
        }

        return {
            left: physicsCoordinate.left,
            bottom: physicsCoordinate.bottom,
            width: physicsCoordinate.width,
            height: physicsCoordinate.height
        };
    }

    let physicsCoordinate: PhysicsCoordinate|null = Store.getPhysicsCoordinate(targetCube);
    let parentId: CubeId = CubeTree.findParentIdByCubeId(targetCube);
    let parentPhysicsCoordinate: PhysicsCoordinate|null = Store.getPhysicsCoordinate(parentId);
    let parentBottomLine: PhysicsMeasurement = 0;

    if (CubeTree.isGoods(targetCube)
        || CubeTree.isBafflePlate(targetCube)
        || CubeTree.isTextarea(targetCube)) {
        if (CubeTree.isCabinet(parentId)) {
            parentBottomLine = parentPhysicsCoordinate.bottom;
        }
        else {
            parentBottomLine = Utils.math(parentPhysicsCoordinate.bottom, parentPhysicsCoordinate.height, '+');
        }
    }

    return {
        left: Utils.math(physicsCoordinate.left, parentPhysicsCoordinate.left, '-'),
        bottom: Utils.math(physicsCoordinate.bottom, parentBottomLine, '-'),
        width: physicsCoordinate.width,
        height: physicsCoordinate.height
    };
}

function getRelativeLogicCoordinate(cubeId: CubeId): LogicRelativeCoordinate|null {
    let physicsCoordinate: PhysicsRelativeCoordinate|null = getRelativePhysicsCoordinate(cubeId);

    if (!physicsCoordinate) {
        return null;
    }

    return {
        left: Toolkit.cmToPx(physicsCoordinate.left),
        bottom: Toolkit.cmToPx(physicsCoordinate.bottom),
        width: Toolkit.cmToPx(physicsCoordinate.width),
        height: Toolkit.cmToPx(physicsCoordinate.height)
    };
}

// 获取cube左下角和右上角的相对坐标
function getDiagonalRelativePhysicsCoordinate(cubeId: CubeId): DiagonalRelativePhysicsCoordinate|null {
    let relativePhysicsCoordinate: PhysicsRelativeCoordinate|null = getRelativePhysicsCoordinate(cubeId);

    if (!relativePhysicsCoordinate) {
        return null;
    }

    let width: PhysicsMeasurement = relativePhysicsCoordinate.width;
    let height: PhysicsMeasurement = relativePhysicsCoordinate.height;
    let left: PhysicsMeasurement = relativePhysicsCoordinate.left;
    let bottom: PhysicsMeasurement = relativePhysicsCoordinate.bottom;
    let right: PhysicsMeasurement = Utils.math(left, width, '+');
    let top: PhysicsMeasurement = Utils.math(bottom, height, '+');
    let leftBottom: Array<PhysicsMeasurement> = [left, bottom];
    let rightTop: Array<PhysicsMeasurement> = [right, top];

    return {
        leftBottom,
        rightTop
    };
}

// 查询指定类型cube的渲染坐标
function getPaintCoordinateByType(type: CubeType): CubePaintCoordinate[] {
    let cubes: CubeId[] = CubeTree.getCubeByType(type);

    return cubes.map((cubeId: CubeId): CubePaintCoordinate => {
        let coordinate: PaintCoordinate = Store.getPaintCoordinate(cubeId);

        return {
            ...coordinate,
            id: cubeId
        }
    });
}

function getLogicCoordinateByType(type: CubeType): CubeLogicCoordinate[] {
    let cubes: CubeId[] = CubeTree.getCubeByType(type);

    return cubes.map((cubeId: CubeId): CubeLogicCoordinate => {
        let coordinate: LogicCoordinate = Store.getLogicCoordinate(cubeId);

        return {
            ...coordinate,
            id: cubeId
        }
    });
}

// 相对坐标计算
function getPhysicsRelative(target: PhysicsCoordinate, base: PhysicsCoordinate): PhysicsCoordinate {
    return {
        left: Utils.math(target.left, base.left, '-'),
        bottom: Utils.math(target.bottom, base.bottom, '-'),
        width: target.width,
        height: target.height
    };
}

// 查询与目标cube左侧对齐的cube
function findSameLeftCube(target: CubeId): CubeId[] {
    let cubeIds: Array<CubeId> = Store.allCubeIds();
    let targetPhysicsCoordinate: PhysicsCoordinate = Store.getPhysicsCoordinate(target);

    return cubeIds.filter((cubeId: CubeId): boolean => {
        let cur: PhysicsCoordinate = Store.getPhysicsCoordinate(cubeId);

        return cubeId !== target && cur.left === targetPhysicsCoordinate.left;
    });
}

export {
    getInTargetLogicRectCubeIds,
    getRelativePhysicsCoordinate,
    getRelativeLogicCoordinate,
    getDiagonalRelativePhysicsCoordinate,
    getInTargetPaintRectCubeIds,
    getLogicCoordinateByType,
    getPaintCoordinateByType,
    getPhysicsRelative,
    filterByTargetPaintRect,
    findSameLeftCube
};
